import { Level } from "../Level.js";
import { GRID_W, GRID_H } from "../../config/constants.js";
import { rndi } from "../../core/util.js";
import { scatterProps, finalize, walkableCells, gdist } from "./helpers.js";

/**
 * Village: a big open green with building footprints laid out around streets
 * and a central plaza. Buildings are blocking props; the streets between them
 * (and a guaranteed perimeter ring) keep everything connected.
 */
export function generateVillage(floor, theme) {
  const level = new Level(GRID_W, GRID_H, theme);
  const margin = 3;
  const x0 = margin, y0 = margin, x1 = GRID_W - margin, y1 = GRID_H - margin;

  // open ground
  for (let y = y0; y < y1; y++)
    for (let x = x0; x < x1; x++) level.setFloor(x, y, (x + y) % 2);

  const cx = Math.floor((x0 + x1) / 2), cy = Math.floor((y0 + y1) / 2);
  const buildings = [];
  const target = 6 + Math.min(floor, 6);
  let tries = 0;
  while (buildings.length < target && tries < 400) {
    tries++;
    const bw = rndi(4, 7), bh = rndi(4, 7);
    // keep a 2-cell street ring inside the ground, and clear central plaza
    const bx = rndi(x0 + 2, x1 - bw - 2), by = rndi(y0 + 2, y1 - bh - 2);
    const bcx = bx + bw / 2, bcy = by + bh / 2;
    if (gdist(bcx, bcy, cx, cy) < 5) continue; // keep the plaza open
    let overlap = false;
    for (const b of buildings) {
      if (bx < b.x + b.w + 2 && bx + bw + 2 > b.x && by < b.y + b.h + 2 && by + bh + 2 > b.y) { overlap = true; break; }
    }
    if (overlap) continue;
    for (let y = by; y < by + bh; y++)
      for (let x = bx; x < bx + bw; x++) level.block[y][x] = 1; // footprint impassable
    buildings.push({ x: bx, y: by, w: bw, h: bh, cx: bcx, cy: bcy, kind: "building" });
    level.addProp({ gx: bcx, gy: bcy, shape: "building", color: 0x9a8060,
      blocking: false, w: bw, h: bh, rot: 0, scale: 1,
      key: theme.assets && theme.assets.building ? theme.assets.building : null });
  }
  level.regions = buildings;

  // entrance at the left edge of the ground, mid height
  level.playerStart = { gx: x0 + 1, gy: cy };

  // spawn candidates + scatter props on open street/plaza cells
  const open = walkableCells(level);
  for (const [gx, gy] of open)
    if (gdist(gx, gy, x0 + 1, cy) > 7) level.spawnPoints.push({ gx: gx + 0.5, gy: gy + 0.5 });

  // a well in the plaza
  level.addProp({ gx: cx + 0.5, gy: cy + 0.5, shape: "well", color: 0x8a8a8a,
    blocking: true, rot: 0, scale: 1 });
  level.block[cy][cx] = 1;

  scatterProps(level, theme, open, theme.propDensity, [[x0 + 1, cy], [cx, cy]]);

  return finalize(level);
}
